import React, { useCallback, useRef } from 'react'
import MultiStepFlow from 'components/MultiStepFlow/MultiStepFlow'
import { Spinner } from 'common/Spinner/Spinner'
import { MultiStepPageLayout } from 'components/MultiStepFlow/MultiStepPageLayout/MultiStepPageLayout'
import { StepDefinition } from '../types'
import { Interpreter, State } from 'xstate'
import Styles from './FlowRenderer.module.scss'

const mapSteps = (steps: StepDefinition[]) => {
  const stateToComponentKey: Record<string, string> = {}
  const componentMap: Record<string, React.FC<any>> = {}
  const stateSteps: Record<string, number> = {}

  steps.forEach((s, i) => {
    stateToComponentKey[s.id] = s.id
    componentMap[s.id] = s.component
    stateSteps[s.id] = i + 1
  })
  return { stateToComponentKey, componentMap, stateSteps }
}

interface Props {
  current: State<any, any>
  send: Interpreter<any, any, any>['send']
  steps: StepDefinition[]
  ctx: Record<string, any>
  isLoading: boolean
  isDone: boolean
  progressPercentage?: number
}

export const FlowRenderer: React.FC<Props> = ({ current, send, steps, ctx, isLoading, isDone, progressPercentage }) => {
  // Use ref to store the current back handler
  const currentStepBackHandlerRef = useRef<(() => void) | null>(null)

  // Callback for step components to register their custom back handler
  const onProvideBackHandler = useCallback((handler: (() => void) | null) => {
    currentStepBackHandlerRef.current = handler
  }, [])

  // Custom back function that uses the registered handler if available
  const customBack = useCallback(() => {
    const handler = currentStepBackHandlerRef.current
    if (handler) {
      handler()
    } else {
      // Fallback to generic BACK event
      send({ type: 'BACK' })
    }
  }, [send])

  const val = typeof current.value === 'string' ? current.value : Object.keys(current.value)[0]

  if (isLoading || isDone) {
    return (
      <MultiStepPageLayout currentStep={0} totalSteps={1} onBack={() => {}} currentStepKey="busy" siderImage="">
        <div className={Styles.spinnerContainer}>
          <Spinner />
        </div>
      </MultiStepPageLayout>
    )
  }

  const maps = mapSteps(steps)

  const idx = steps.findIndex((s) => s.id === val)
  const stepDef = steps[idx] ?? steps[0]
  const stepNum = idx + 1

  const image = typeof stepDef.image === 'function' ? stepDef.image(ctx) : stepDef.image ?? ''
  const siderComponent = stepDef.siderComponent ?? undefined
  const backgroundClassName = stepDef.backgroundClassName

  return (
    <MultiStepFlow
      current={current}
      send={send}
      {...maps}
      totalSteps={steps.length}
      currentStep={stepNum}
      showBackButton={!stepDef.noBackButton}
      onSkip={stepDef.skipAllowed ? () => send('SKIP') : undefined}
      siderImage={image}
      siderComponent={siderComponent}
      getExtraStepProps={(k) => ({
        ...(steps.find((s) => s.id === k)?.extra?.(ctx) ?? {}),
        onProvideBackHandler,
      })}
      progressPercentage={progressPercentage}
      fullWidth={stepDef.fullWidth}
      fullScreen={stepDef.fullScreen}
      backgroundClassName={backgroundClassName}
      showMobileLogo={stepDef.showMobileLogo}
      customBack={customBack}
    />
  )
}
